import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Save, Loader2, Download, Megaphone, Pencil, Facebook, Instagram, Music2, Chrome, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { CampaignAuditTrail } from "@/components/CampaignAuditTrail";
import { AudienceInsights } from "@/components/AudienceInsights";
import { CostAlertsCard } from "@/components/CostAlertsCard";
import { BudgetOptimizerCard } from "@/components/BudgetOptimizerCard";
import { ABTestCard } from "@/components/ABTestCard";
import { buildClientReport, type ReportBestAd } from "@/lib/client-report";
import { LockedFeatureCard } from "@/components/LockedFeatureCard";
import { usePlanAccess } from "@/hooks/usePlanAccess";

type Campaign = {
  id: string;
  name: string;
  platform: string;
  status: string;
  created_at: string;
  budget_type: string | null;
  daily_budget: number | null;
  total_budget: number | null;
  duration_days: number | null;
  client_id: string | null;
};

type Metric = {
  id: string;
  campaign_id: string;
  spend: number;
  reach: number;
  clicks: number;
  leads: number;
  conversions: number;
  revenue: number;
  recorded_at: string;
};

export const Route = createFileRoute("/_app/campaigns/$campaignId")({
  component: CampaignDetailPage,
  head: () => ({
    meta: [
      { title: "Campaign Details · AdKavi" },
      { name: "description", content: "Edit budget, view performance, and export campaign reports." },
    ],
  }),
});

function CampaignDetailPage() {
  const { campaignId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { hasFeature, requiredPlanFor } = usePlanAccess();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Editable fields
  const [budgetType, setBudgetType] = useState<"daily" | "total">("daily");
  const [dailyBudget, setDailyBudget] = useState("");
  const [totalBudget, setTotalBudget] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<"pdf" | "csv">("pdf");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      supabase.from("campaigns").select("*").eq("id", campaignId).eq("user_id", user.id).maybeSingle(),
      supabase.from("campaign_metrics").select("*").eq("campaign_id", campaignId).eq("user_id", user.id).order("recorded_at", { ascending: false }),
    ]).then(([cRes, mRes]) => {
      if (cancelled) return;
      if (cRes.error) {
        console.error(cRes.error);
        toast.error(t("campaign.toast.loadFailed"));
        return;
      }
      const c = cRes.data as Campaign | null;
      if (!c) {
        toast.error(t("campaign.toast.notFound"));
        navigate({ to: "/campaigns" });
        return;
      }
      setCampaign(c);
      setBudgetType((c.budget_type as "daily" | "total") ?? "daily");
      setDailyBudget(c.daily_budget != null ? String(c.daily_budget) : "");
      setTotalBudget(c.total_budget != null ? String(c.total_budget) : "");
      setDurationDays(c.duration_days != null ? String(c.duration_days) : "");
      setMetrics((mRes.data as Metric[]) ?? []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user, campaignId, navigate]);

  const computedTotal = useMemo(() => {
    if (budgetType === "daily") return Number(dailyBudget || 0) * Number(durationDays || 0);
    return Number(totalBudget || 0);
  }, [budgetType, dailyBudget, totalBudget, durationDays]);
  const estimatedReach = Math.max(0, Math.round((computedTotal / 8) * 1000));

  const totals = useMemo(() => {
    return metrics.reduce(
      (a, m) => {
        a.spend += Number(m.spend) || 0;
        a.reach += m.reach || 0;
        a.clicks += m.clicks || 0;
        a.leads += m.leads || 0;
        a.conversions += m.conversions || 0;
        a.revenue += Number(m.revenue) || 0;
        return a;
      },
      { spend: 0, reach: 0, clicks: 0, leads: 0, conversions: 0, revenue: 0 }
    );
  }, [metrics]);

  const totalBudgetValue = campaign
    ? campaign.budget_type === "daily"
      ? (campaign.daily_budget ?? 0) * (campaign.duration_days ?? 0)
      : (campaign.total_budget ?? 0)
    : 0;
  const spendPct = totalBudgetValue > 0 ? Math.min(100, (totals.spend / totalBudgetValue) * 100) : 0;
  const remaining = Math.max(0, totalBudgetValue - totals.spend);

  const PLATFORM_META: Array<{ id: string; label: string; icon: React.ComponentType<{ className?: string }>; matches: (p: string) => boolean }> = [
    { id: "meta", label: "Meta (Facebook & Instagram)", icon: Facebook, matches: (p) => ["meta", "facebook", "instagram"].includes(p) },
    { id: "tiktok", label: "TikTok", icon: Music2, matches: (p) => p === "tiktok" },
    { id: "google", label: "Google Ads", icon: Chrome, matches: (p) => p === "google" },
    { id: "youtube", label: "YouTube", icon: Youtube, matches: (p) => p === "youtube" },
  ];
  const platformBreakdown = PLATFORM_META.map((pm) => {
    const active = campaign ? pm.matches(campaign.platform) : false;
    return {
      id: pm.id,
      label: pm.label,
      icon: pm.icon,
      active,
      budget: active ? totalBudgetValue : 0,
      spend: active ? totals.spend : 0,
    };
  });

  const requestSave = () => {
    if (!user || !campaign) return;
    if (Number(durationDays) <= 0) { toast.error(t("campaign.toast.durationMin")); return; }
    if (budgetType === "daily" && Number(dailyBudget) <= 0) { toast.error(t("campaign.toast.dailyMin")); return; }
    if (budgetType === "total" && Number(totalBudget) <= 0) { toast.error(t("campaign.toast.totalMin")); return; }
    setConfirmOpen(true);
  };

  const confirmSave = async () => {
    if (!user || !campaign) return;
    setConfirmOpen(false);
    setSaving(true);
    const { error } = await supabase
      .from("campaigns")
      .update({
        budget_type: budgetType,
        daily_budget: budgetType === "daily" ? Number(dailyBudget) : null,
        total_budget: budgetType === "daily" ? Number(dailyBudget) * Number(durationDays) : Number(totalBudget),
        duration_days: Number(durationDays),
      })
      .eq("id", campaign.id)
      .eq("user_id", user.id);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(t("campaign.toast.budgetUpdated"));
    setCampaign({
      ...campaign,
      budget_type: budgetType,
      daily_budget: budgetType === "daily" ? Number(dailyBudget) : null,
      total_budget: budgetType === "daily" ? Number(dailyBudget) * Number(durationDays) : Number(totalBudget),
      duration_days: Number(durationDays),
    });
    setEditing(false);
  };

  const exportCSV = () => {
    if (!campaign) return;
    const rows: string[][] = [
      ["Field", "Value"],
      ["Campaign Name", campaign.name],
      ["Platform", campaign.platform],
      ["Status", campaign.status],
      ["Created", new Date(campaign.created_at).toLocaleString()],
      ["Budget Type", campaign.budget_type ?? "—"],
      ["Daily Budget", campaign.daily_budget != null ? `$${campaign.daily_budget}` : "—"],
      ["Total Budget", `$${totalBudgetValue.toLocaleString()}`],
      ["Duration (days)", String(campaign.duration_days ?? "—")],
      ["Estimated Reach", `${Math.max(0, Math.round((totalBudgetValue / 8) * 1000)).toLocaleString()} people`],
      [],
      ["Performance", ""],
      ["Spend", `$${totals.spend.toFixed(2)}`],
      ["Remaining", `$${remaining.toFixed(2)}`],
      ["Reach", String(totals.reach)],
      ["Clicks", String(totals.clicks)],
      ["Leads", String(totals.leads)],
      ["Conversions", String(totals.conversions)],
      ["Revenue", `$${totals.revenue.toFixed(2)}`],
      ["ROAS", totals.spend > 0 ? (totals.revenue / totals.spend).toFixed(2) : "—"],
      [],
      ["Platform Breakdown", ""],
      ...platformBreakdown.map((p) => [p.label, `Budget $${p.budget.toLocaleString()} · Spend $${p.spend.toFixed(2)}${p.active ? " (active)" : ""}`]),
    ];
    const csv = rows
      .map((r) => r.map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${campaign.name.replace(/[^a-z0-9]+/gi, "_")}_report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!campaign || !user) return;
    setExporting(true);
    try {
      // Determine date range from metrics (fall back to campaign created → today)
      const sortedMetrics = [...metrics].sort(
        (a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime(),
      );
      const start =
        sortedMetrics.length > 0
          ? new Date(sortedMetrics[0].recorded_at)
          : new Date(campaign.created_at);
      const end =
        sortedMetrics.length > 0
          ? new Date(sortedMetrics[sortedMetrics.length - 1].recorded_at)
          : new Date();

      // Pull ads + ad_metrics to find best performer (by ROAS, fallback to revenue)
      let bestAd: ReportBestAd = null;
      const { data: adsData } = await supabase
        .from("ads")
        .select("id, name")
        .eq("campaign_id", campaign.id)
        .eq("user_id", user.id);

      if (adsData && adsData.length > 0) {
        const adIds = adsData.map((a) => a.id);
        const { data: adMetrics } = await supabase
          .from("ad_metrics")
          .select("ad_id, spend, clicks, conversions, revenue")
          .in("ad_id", adIds);
        const byAd = new Map<
          string,
          { spend: number; clicks: number; conversions: number; revenue: number }
        >();
        (adMetrics ?? []).forEach((m) => {
          const cur = byAd.get(m.ad_id) ?? { spend: 0, clicks: 0, conversions: 0, revenue: 0 };
          cur.spend += Number(m.spend) || 0;
          cur.clicks += m.clicks || 0;
          cur.conversions += m.conversions || 0;
          cur.revenue += Number(m.revenue) || 0;
          byAd.set(m.ad_id, cur);
        });
        const ranked = adsData
          .map((a) => {
            const m = byAd.get(a.id) ?? { spend: 0, clicks: 0, conversions: 0, revenue: 0 };
            const roas = m.spend > 0 ? m.revenue / m.spend : 0;
            return { name: a.name, ...m, roas };
          })
          .filter((a) => a.spend > 0 || a.revenue > 0)
          .sort((a, b) => b.roas - a.roas || b.revenue - a.revenue);
        if (ranked.length > 0) bestAd = ranked[0];
      }

      // Ask Claude for an executive summary (best-effort; report still renders without it)
      let aiSummary = "";
      try {
        const { data: sumRes, error: sumErr } = await supabase.functions.invoke(
          "generate-campaign-summary",
          {
            body: {
              campaign_name: campaign.name,
              platform: campaign.platform,
              lang,
              date_range: `${start.toISOString().slice(0, 10)} → ${end.toISOString().slice(0, 10)}`,
              totals,
              best_ad: bestAd,
              platform_breakdown: platformBreakdown
                .filter((p) => p.active)
                .map(({ label, budget, spend, active }) => ({ label, budget, spend, active })),
            },
          },
        );
        if (!sumErr && sumRes?.summary) aiSummary = sumRes.summary as string;
      } catch (e) {
        console.warn("AI summary failed", e);
      }

      const doc = buildClientReport({
        campaignName: campaign.name,
        platform: campaign.platform,
        status: campaign.status,
        dateRange: { start, end },
        totalBudget: totalBudgetValue,
        totals,
        platformBreakdown: platformBreakdown.map(({ label, budget, spend, active }) => ({
          label,
          budget,
          spend,
          active,
        })),
        bestAd,
        aiSummary,
        lang: lang === "es" ? "es" : "en",
      });

      doc.save(`${campaign.name.replace(/[^a-z0-9]+/gi, "_")}_report.pdf`);
    } catch (e) {
      console.error(e);
      toast.error((e as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const handleExport = () => {
    if (exportFormat === "csv") exportCSV();
    else exportPDF();
  };

  if (!user) return null;
  if (loading) {
    return (
      <section className="max-w-4xl mx-auto px-6 py-10">
        <div className="rounded-2xl border border-border/50 bg-card/40 p-10 text-center text-muted-foreground">
          {t("campaign.loading")}
        </div>
      </section>
    );
  }
  if (!campaign) return null;

  const fmtMoney = (n: number) => `$${n.toLocaleString()}`;
  const fmtMoney2 = (n: number) => `$${n.toFixed(2)}`;

  return (
    <section className="max-w-4xl mx-auto px-6 py-8">
      <Link to="/campaigns" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {t("campaign.back")}
      </Link>

      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Megaphone className="w-7 h-7 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{campaign.name}</span>
          </h1>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant={campaign.status === "active" ? "default" : "secondary"}>{t(`campaignsList.status.${campaign.status}`) || campaign.status}</Badge>
            <span className="text-sm text-muted-foreground capitalize">{campaign.platform}</span>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={exportFormat} onValueChange={(v) => setExportFormat(v as "pdf" | "csv")}>
            <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pdf">PDF</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {t("campaign.export")}
          </Button>
        </div>
      </div>

      {/* Spend vs Budget */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t("campaign.budget.title")}</CardTitle>
          <CardDescription>{t("campaign.budget.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium">{t("campaign.budget.spent", { amount: fmtMoney2(totals.spend) })}</span>
            <span className="text-muted-foreground">{t("campaign.budget.budget", { amount: fmtMoney(totalBudgetValue) })}</span>
          </div>
          <Progress value={spendPct} />
          <div className="mt-2 flex justify-between text-xs text-muted-foreground">
            <span>{t("campaign.budget.used", { pct: spendPct.toFixed(1) })}</span>
            <span>{t("campaign.budget.remaining", { amount: fmtMoney2(remaining) })}</span>
          </div>
        </CardContent>
      </Card>

      {/* Platform Spend Breakdown */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t("campaign.platform.title")}</CardTitle>
          <CardDescription>{t("campaign.platform.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {platformBreakdown.map((p) => {
              const Icon = p.icon;
              const pct = p.budget > 0 ? Math.min(100, (p.spend / p.budget) * 100) : 0;
              return (
                <div key={p.id} className="rounded-lg border border-border/50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{p.label}</span>
                      {p.active && <Badge variant="secondary" className="text-xs">{t("campaign.platform.active")}</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fmtMoney2(p.spend)} <span className="opacity-60">/ {fmtMoney(p.budget)}</span>
                    </div>
                  </div>
                  <Progress value={pct} className={p.active ? "" : "opacity-40"} />
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("campaign.platform.note")}
          </p>
        </CardContent>
      </Card>

      {/* Audience Insights (mock data) */}
      <div className="mb-6">
        <AudienceInsights seed={campaign.id} compact />
      </div>

      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t("campaign.details.title")}</CardTitle>
            <CardDescription>{t("campaign.details.desc")}</CardDescription>
          </div>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Pencil className="w-4 h-4 mr-2" /> {t("campaign.details.edit")}</Button>
          )}
        </CardHeader>
        <CardContent className="space-y-5">
          {!editing ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("campaign.field.budgetType")} value={campaign.budget_type ?? "—"} className="capitalize" />
              <Field label={t("campaign.field.dailyBudget")} value={campaign.daily_budget != null ? t("campaign.details.dailyValue", { amount: fmtMoney(Number(campaign.daily_budget)) }) : "—"} />
              <Field label={t("campaign.field.totalBudget")} value={fmtMoney(totalBudgetValue)} />
              <Field label={t("campaign.field.duration")} value={t("campaign.details.daysValue", { n: campaign.duration_days ?? 0 })} />
              <Field label={t("campaign.field.estimatedReach")} value={t("campaign.details.people", { n: Math.max(0, Math.round((totalBudgetValue / 8) * 1000)).toLocaleString() })} />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block">{t("campaign.details.budgetType")}</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button type="button" onClick={() => setBudgetType("daily")}
                    className={`rounded-xl border-2 p-3 text-left text-sm transition ${budgetType === "daily" ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"}`}>
                    <p className="font-semibold">{t("campaign.details.budgetTypeDaily")}</p>
                  </button>
                  <button type="button" onClick={() => setBudgetType("total")}
                    className={`rounded-xl border-2 p-3 text-left text-sm transition ${budgetType === "total" ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"}`}>
                    <p className="font-semibold">{t("campaign.details.budgetTypeTotal")}</p>
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {budgetType === "daily" ? (
                  <div>
                    <Label htmlFor="daily">{t("campaign.details.dailyLabel")}</Label>
                    <Input id="daily" type="number" min={1} value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="total">{t("campaign.details.totalLabel")}</Label>
                    <Input id="total" type="number" min={1} value={totalBudget} onChange={(e) => setTotalBudget(e.target.value)} />
                  </div>
                )}
                <div>
                  <Label htmlFor="dur">{t("campaign.details.durationLabel")}</Label>
                  <Input id="dur" type="number" min={1} value={durationDays} onChange={(e) => setDurationDays(e.target.value)} />
                </div>
              </div>
              <Separator />
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("campaign.details.newTotal")}</p>
                  <p className="text-lg font-semibold">{fmtMoney(computedTotal)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("campaign.details.newReach")}</p>
                  <p className="text-lg font-semibold">{t("campaign.details.people", { n: estimatedReach.toLocaleString() })}</p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setEditing(false)}>{t("campaign.details.cancel")}</Button>
                <Button onClick={requestSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t("campaign.details.save")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Trail */}
      <CampaignAuditTrail
        campaignId={campaign.id}
        campaignName={campaign.name}
        createdAt={campaign.created_at}
        status={campaign.status}
      />

      {/* Cost alerts */}
      <CostAlertsCard campaignId={campaign.id} clientId={campaign.client_id} />

      {/* AI Budget Optimizer */}
      {hasFeature("budget_optimizer") ? (
        <BudgetOptimizerCard campaignId={campaign.id} clientId={campaign.client_id} />
      ) : (
        <LockedFeatureCard
          title={t("plan.feature.budgetOptimizer")}
          requiredPlan={requiredPlanFor("budget_optimizer")}
        />
      )}

      {/* A/B Testing */}
      {hasFeature("ab_testing") ? (
        <ABTestCard campaignId={campaign.id} clientId={campaign.client_id} />
      ) : (
        <LockedFeatureCard
          title={t("plan.feature.abTesting")}
          requiredPlan={requiredPlanFor("ab_testing")}
        />
      )}

      {/* Performance summary */}
      <Card>
        <CardHeader>
          <CardTitle>{t("campaign.perf.title")}</CardTitle>
          <CardDescription>{t("campaign.perf.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label={t("campaign.perf.reach")} value={totals.reach.toLocaleString()} />
            <Field label={t("campaign.perf.clicks")} value={totals.clicks.toLocaleString()} />
            <Field label={t("campaign.perf.leads")} value={totals.leads.toLocaleString()} />
            <Field label={t("campaign.perf.conversions")} value={totals.conversions.toLocaleString()} />
            <Field label={t("campaign.perf.revenue")} value={fmtMoney2(totals.revenue)} />
            <Field label={t("campaign.perf.spend")} value={fmtMoney2(totals.spend)} />
            <Field label={t("campaign.perf.roas")} value={totals.spend > 0 ? `${(totals.revenue / totals.spend).toFixed(2)}x` : "—"} />
          </div>
        </CardContent>
      </Card>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("campaign.confirm.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {budgetType === "daily"
                ? t("campaign.confirm.dailyBody", {
                    daily: fmtMoney(Number(dailyBudget || 0)),
                    days: durationDays || "0",
                    total: fmtMoney(Number(dailyBudget || 0) * Number(durationDays || 0)),
                  })
                : t("campaign.confirm.totalBody", {
                    total: fmtMoney(Number(totalBudget || 0)),
                    days: durationDays || "0",
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("campaign.confirm.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSave}>{t("campaign.confirm.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-sm font-medium ${className ?? ""}`}>{value || "—"}</p>
    </div>
  );
}
